import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, type ApolloError } from "@apollo/client";
import DetailsCard from "@/components/DetailsCard";
import SubSectionHeading from "@/components/SubSectionHeading";
import MultiSelect from "@/components/MultiSelect";
import Select from "@/components/Select";
import Chip, { ChipSet } from "@/components/Chip";
import EmptyState from "@/components/EmptyState";
import MetricGrid from "@/components/MetricGrid";
import DateRangeScroller, {
  parsePeriodFromSearch,
  periodToSearch,
  type PeriodWindow
} from "@/components/DateRangeScroller";
import { InteractionMode } from "@/components/interactions/types";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { sendToRaygun } from "@/lib/raygunUtils";
import { toDateFormat } from "@/lib/dateHelpers";
import { CAREER_SERVICES_INTERACTION_STATS_QUERY } from "./queries";
import CareerCharts from "./_components/CareerCharts";
import CareerHighlights from "./_components/CareerHighlights";
import RecentInteractionsCard from "./_components/RecentInteractionsCard";
import TeamLeaderboard from "./_components/TeamLeaderboard";
import {
  buildOwnerOptions,
  filterCareerServicesStats,
  interactionsDeltaText,
  ownerDisplayName,
  teamAverageInteractions,
  teamTotalInteractions
} from "./_utils";
import { formatCount, formatPercent, percentOf } from "../_utils";

const PAGE_PATH = "/analytics/interactions/career-services";

const MODE_OPTIONS: { value: string | null; label: string }[] = [
  { value: null, label: "All modes" },
  ...Object.values(InteractionMode).map((mode) => ({ value: mode as string, label: mode }))
];

export default function CareerServicesTabPage() {
  usePageTitle(PAGE_PATH, "Interaction Analytics | Career Services");
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewCareerServicesDashboard });

  const [searchParams, setSearchParams] = useSearchParams();
  const period = useMemo(() => parsePeriodFromSearch(searchParams), [searchParams]);
  // Null categories = all selected. Resolved once stats load so the toggle-all affordance works.
  const [categories, setCategories] = useState<string[] | null>(null);
  const [mode, setMode] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const { setError, clearError } = useErrorBanner();

  const startDateTime = period.start.toUTC().toISO() ?? "";
  const endDateTime = period.end.toUTC().toISO() ?? "";

  const { data, previousData, loading, error, refetch } = useQuery(CAREER_SERVICES_INTERACTION_STATS_QUERY, {
    variables: { startDateTime, endDateTime, ownerId },
    skip: !startDateTime || !endDateTime,
    notifyOnNetworkStatusChange: true,
    onCompleted: () => clearError(),
    onError: (queryError: ApolloError) => {
      sendToRaygun(queryError, { tags: ["career-services", "stats"] });
      setError(queryError.message);
    }
  });

  const stats = (data ?? previousData)?.careerServicesInteractionStats;
  const owners = useMemo(() => stats?.owners ?? [], [stats]);
  const ownerOptions = useMemo(() => buildOwnerOptions(owners), [owners]);
  const teamAvgInteractions = useMemo(() => teamAverageInteractions(owners), [owners]);
  const teamInteractions = useMemo(() => teamTotalInteractions(owners), [owners]);
  const selectedOwner = ownerId ? (owners.find((entry) => entry.ownerId === ownerId) ?? null) : null;
  const allCategories = useMemo(() => (stats?.categories ?? []).map((entry) => entry.category), [stats]);
  const selectedCategories = categories ?? allCategories;
  const filtered = useMemo(
    () => filterCareerServicesStats(stats, selectedCategories, mode),
    [stats, selectedCategories, mode]
  );
  const categoryCounts = useMemo(
    () => new Map((stats?.categories ?? []).map((entry) => [entry.category, entry.count])),
    [stats]
  );
  const categoryOptions = useMemo(
    () =>
      allCategories.map((category) => ({
        value: category,
        label: `${category} (${categoryCounts.get(category) ?? 0})`
      })),
    [allCategories, categoryCounts]
  );
  const latestInteractionAt = stats?.recentInteractions[0]?.interactionDate;
  const busy = loading && !stats;

  const updatePeriod = (next: PeriodWindow) => {
    setSearchParams(periodToSearch(next), { replace: true });
  };

  if (error && !stats) {
    return (
      <EmptyState
        title="Unable to load Career Services interactions"
        caption="Try refreshing the page. If the problem continues, contact support."
        iconName="error"
        action={{ children: "Try Again", onClick: () => refetch() }}
      />
    );
  }

  return (
    <div data-testid="career-services-page">
      <DetailsCard className="mt-12">
        <div className="flex w-full flex-col gap-6">
          <div className="flex w-full flex-col items-start gap-4">
            <SubSectionHeading>Filter By Date Range</SubSectionHeading>
            <DateRangeScroller value={period} onChange={updatePeriod} />
          </div>
          <div className="flex w-full flex-col items-start gap-4">
            <SubSectionHeading>Filter By Category</SubSectionHeading>
            <div className="flex w-full flex-wrap items-center gap-4">
              <div className="shrink-0">
                <MultiSelect
                  label="Categories"
                  options={categoryOptions}
                  selectedValues={selectedCategories}
                  selectedText={
                    categories === null
                      ? "All categories"
                      : selectedCategories.length === 0
                        ? "None selected"
                        : selectedCategories.length === allCategories.length
                          ? "All categories"
                          : `${selectedCategories.length} selected`
                  }
                  selectAllLabel="Select All"
                  selectNoneLabel="Select None"
                  closeOnSelectAll
                  searchable
                  onSelect={(value, checked) => {
                    const baseline = categories ?? allCategories;
                    setCategories(
                      checked
                        ? [...baseline, value].filter((category, index, list) => list.indexOf(category) === index)
                        : baseline.filter((category) => category !== value)
                    );
                  }}
                  onClear={() => setCategories(null)}
                  onSelectNone={() => setCategories([])}
                  className="w-[18rem] max-w-full"
                />
              </div>
              <div role="group" aria-label="Mode" className="min-w-0">
                <ChipSet>
                  {MODE_OPTIONS.map((option) => (
                    <Chip
                      key={option.label}
                      label={option.label}
                      selected={mode === option.value}
                      onClick={() => setMode(option.value)}
                    />
                  ))}
                </ChipSet>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-4">
            <SubSectionHeading>Filter By Team Member</SubSectionHeading>
            <div className="w-[18rem] max-w-full">
              <Select
                label="Team member"
                options={ownerOptions}
                value={ownerId ?? ""}
                placeholder="All members"
                searchable
                onSelect={setOwnerId}
                onClear={() => setOwnerId(null)}
                data-testid="career-services-owner-select"
              />
            </div>
          </div>
        </div>
      </DetailsCard>

      <MetricGrid
        layout="expanded"
        metrics={[
          {
            label: "Total interactions",
            value: busy ? undefined : formatCount(filtered.total),
            loading: busy,
            iconName: "forum",
            testId: "career-services-total-metric",
            tooltip: {
              supportingText: selectedOwner
                ? `${ownerDisplayName(selectedOwner.ownerName)} logged ${formatCount(filtered.total)} of ${formatCount(teamInteractions)} team interactions (${formatPercent(percentOf(filtered.total, teamInteractions))}, ${interactionsDeltaText(filtered.total, teamAvgInteractions)}).`
                : "Career Services interactions in range matching the selected mode."
            }
          },
          {
            label: "Top category",
            value: busy ? undefined : (filtered.topCategory?.category ?? "—"),
            loading: busy,
            iconName: "category",
            testId: "career-services-top-category-metric",
            tooltip: {
              supportingText: filtered.topCategory
                ? `${formatCount(filtered.topCategory.count)} interactions in range.`
                : "No interactions match the current filters."
            }
          },
          {
            label: "Top mode",
            value: busy ? undefined : (filtered.topMode ?? "—"),
            loading: busy,
            iconName: "call",
            testId: "career-services-top-mode-metric",
            tooltip: { supportingText: "Most used interaction mode in range." }
          },
          {
            label: "Active days",
            value: busy ? undefined : formatCount(filtered.activeDays),
            loading: busy,
            iconName: "calendar_month",
            testId: "career-services-active-days-metric",
            tooltip: { supportingText: "Days in range with at least one matching interaction." }
          }
        ]}
      />
      {latestInteractionAt && (
        <p className="text-body-medium text-sys-brand-on-surface-variant mt-4" data-testid="career-services-freshness">
          Latest interaction {toDateFormat(latestInteractionAt)}
        </p>
      )}

      <div className="mt-8">
        <CareerHighlights
          startDateTime={startDateTime}
          endDateTime={endDateTime}
          categories={selectedCategories}
          mode={mode}
          ownerId={ownerId}
          enabled
        />
      </div>

      <CareerCharts byMode={filtered.byMode} categories={filtered.categories} days={filtered.days} loading={busy} />

      <div className="pt-12">
        <TeamLeaderboard
          owners={owners}
          teamAvgInteractions={teamAvgInteractions}
          selectedOwnerId={ownerId}
          onSelectOwner={setOwnerId}
          loading={busy}
        />
      </div>

      <div className="pt-12">
        <RecentInteractionsCard interactions={filtered.recentInteractions} loading={busy} />
      </div>
    </div>
  );
}
